import React, { useState, useContext, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  TextField, 
  Typography, 
  Grid,
  InputAdornment,
  Divider,
  Box
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateRangePicker } from "rsuite";
import { 
  HealthAndSafety as InsuranceIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Badge as PolicyIcon,
  Group as GroupIcon,
  MedicalServices as BotoxIcon,
  Today as DateIcon,
  CalendarMonth as DateRangeIcon,
  People as RelationshipIcon
} from "@mui/icons-material";
import { Context } from "../../context";
import { MEDICAL_BILLING_PERSON } from "../../config";

const InsuranceField = ({
  label,
  name,
  value,
  updateInsuranceInfo,
  painConsultNoteSigned,
  icon,
  placeholder,
  type = "text",
  multiline = false
}) => {
  const { user } = useContext(Context);
  
  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      margin="dense"
      label={label}
      value={value || ""}
      name={name}
      onChange={(e) => updateInsuranceInfo(e)}
      disabled={(user && user.role === MEDICAL_BILLING_PERSON) }
      placeholder={placeholder}
      type={type}
      multiline={multiline}
      rows={multiline ? 2 : 1}
      InputProps={{
        startAdornment: icon ? (
          <InputAdornment position="start">
            {icon}
          </InputAdornment>
        ) : null
      }}
    />
  );
};

const InsuranceInformation = ({
  insuranceInformation,
  setInsuranceInformation,
  painConsultNoteSigned,
}) => {
  const { user } = useContext(Context);
  const [botoxAuthorizationValidityDates, setBotoxAuthorizationValidityDates] = useState();
  
  const updateInsuranceInformation = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInsuranceInformation((values) => ({ ...values, [name]: value }));
  };

  const handleDateOfBirthOfSubscriberChange = (date) => {
    setInsuranceInformation({
      ...insuranceInformation,
      date_of_birth_of_subscriber: date,
    });
  };

  const handleBotoxAuthorizationValidityDatesChange = (value) => {
    setBotoxAuthorizationValidityDates(value);
    const formattedRange = value ? {
      start: value[0]?.toISOString(),
      end: value[1]?.toISOString(),
    } : "";
    setInsuranceInformation({
      ...insuranceInformation,
      botox_authorization_validity_dates: formattedRange,
    });
  };

  useEffect(() => {
    const dates = insuranceInformation.botox_authorization_validity_dates;
    if (dates) {
      setBotoxAuthorizationValidityDates([
        new Date(dates.start),
        new Date(dates.end),
      ]);
    }
  }, [insuranceInformation]);

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <InsuranceIcon sx={{ mr: 1 }} color="primary" /> 
          Insurance Information
        </Typography>
        
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          {/* Primary Insurance Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium" color="primary" gutterBottom>
              Primary Insurance Details
            </Typography>
            
            <InsuranceField
              label="Insurance Provider"
              name="insurance_provider"
              value={insuranceInformation.insurance_provider}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<InsuranceIcon color="primary" fontSize="small" />}
              placeholder="Name of insurance company"
            />
            
            <InsuranceField
              label="Policy Number"
              name="policy_number"
              value={insuranceInformation.policy_number}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<PolicyIcon color="primary" fontSize="small" />}
              placeholder="Insurance policy number"
            />
            
            <InsuranceField
              label="Group Number"
              name="group_number"
              value={insuranceInformation.group_number}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<GroupIcon color="primary" fontSize="small" />}
              placeholder="Insurance group number"
            />
            
            <InsuranceField
              label="Subscriber's Name"
              name="name_of_subscriber"
              value={insuranceInformation.name_of_subscriber}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<PersonIcon color="primary" fontSize="small" />}
              placeholder="Full name of subscriber"
            />
            
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium" color="primary" gutterBottom>
                Botox Authorization
              </Typography>
            </Box>
            
            <InsuranceField
              label="Authorization Number"
              name="botox_authorization"
              value={insuranceInformation.botox_authorization}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<BotoxIcon color="primary" fontSize="small" />}
              placeholder="Botox authorization number"
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <DateRangeIcon fontSize="small" color="primary" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Authorization Validity Period
              </Typography>
              <Box sx={{ pl: 3 }}>
                <DateRangePicker
                  format="MM/dd/yyyy"
                  character=" - "
                  block
                  style={{ width: '100%' }}
                  value={botoxAuthorizationValidityDates}
                  onChange={handleBotoxAuthorizationValidityDatesChange}
                  disabled={(user && user.role === MEDICAL_BILLING_PERSON)}
                />
              </Box>
            </Box>
          </Grid>
          
          {/* Subscriber Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium" color="primary" gutterBottom>
              Subscriber Information
            </Typography>
            
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <DateIcon fontSize="small" color="primary" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Subscriber's Date of Birth
              </Typography>
              <Box sx={{ pl: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    onChange={handleDateOfBirthOfSubscriberChange}
                    value={insuranceInformation.date_of_birth_of_subscriber}
                    sx={{ width: '100%' }}
                    disabled={(user && user.role === MEDICAL_BILLING_PERSON)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'MM/DD/YYYY'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            
            <InsuranceField
              label="Relationship to Patient"
              name="relationship_to_the_patient"
              value={insuranceInformation.relationship_to_the_patient}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<RelationshipIcon color="primary" fontSize="small" />}
              placeholder="e.g. Self, Spouse, Child, Other"
            />
            
            <InsuranceField
              label="Subscriber's Address"
              name="address_of_subscriber"
              value={insuranceInformation.address_of_subscriber}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<HomeIcon color="primary" fontSize="small" />}
              placeholder="Complete subscriber address"
              multiline={true}
            />
            
            <InsuranceField
              label="Subscriber's Phone Number"
              name="phone_number_of_subscriber"
              value={insuranceInformation.phone_number_of_subscriber}
              updateInsuranceInfo={updateInsuranceInformation}
              painConsultNoteSigned={painConsultNoteSigned}
              icon={<PhoneIcon color="primary" fontSize="small" />}
              placeholder="(555) 123-4567"
              type="tel"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default InsuranceInformation;